// Cliente HTTP minimo — fetch wrapper que injeta Authorization automatic
// e parseia JSON. Erros HTTP viram exceptions tipadas com status code
// pra UI mostrar mensagem certa (401 = senha errada, etc).

import { get } from 'svelte/store';
import { token, clearSession } from '../stores/auth';
import { DEFAULT_API_BASE } from './constants';

// Sinaliza que a sessao expirou (backend rejeitou token via 401 com
// token presente). Componente top-level subscreve e mostra a tela de
// login com mensagem. Causa em prod: Cloud Run cold start zera as
// sessions em memoria — qualquer redeploy/scale-zero invalida todos
// os tokens ativos. Sem este sinal o usuario veria erro generico ou
// loop infinito de reconexao WS.
import { writable } from 'svelte/store';
export const sessionExpiredFlag = writable(false);

let _sessionExpiredFired = false;
export function triggerSessionExpired(reason?: string): void {
  if (_sessionExpiredFired) return;
  if (!get(token)) return; // ja deslogado, nao faz nada
  _sessionExpiredFired = true;
  if (reason) console.warn('auth:expired —', reason);
  void clearSession();
  sessionExpiredFlag.set(true);
  // permite re-disparar apos novo login
  setTimeout(() => { _sessionExpiredFired = false; }, 1000);
}

export class ApiError extends Error {
  status: number;
  body?: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T = unknown>(
  method: Method,
  path: string,
  body?: unknown,
  overrideToken?: string,
): Promise<T> {
  const base = DEFAULT_API_BASE;
  const tk = overrideToken ?? get(token);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (tk) headers['Authorization'] = `Bearer ${tk}`;

  const res = await fetch(`${base}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = text;
  }

  if (!res.ok) {
    const msg =
      (typeof parsed === 'object' && parsed && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : `HTTP ${res.status}`) || `HTTP ${res.status}`;
    // 401 com token presente E nao e o /token (login):
    // sessao expirou no backend (cold start zerou as sessions). Dispara
    // o flag global pra App.svelte voltar pra tela de login.
    if (res.status === 401 && get(token) && path !== '/token') {
      triggerSessionExpired(msg);
    }
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}

export const api = {
  get: <T = unknown>(path: string, overrideToken?: string) =>
    request<T>('GET', path, undefined, overrideToken),
  post: <T = unknown>(path: string, body?: unknown, overrideToken?: string) =>
    request<T>('POST', path, body, overrideToken),
  put: <T = unknown>(path: string, body?: unknown) =>
    request<T>('PUT', path, body),
  delete: <T = unknown>(path: string) => request<T>('DELETE', path),
};
